import * as Dialog from "@radix-ui/react-dialog"
import { Loader2, X } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/primitives"
import { fetchAgreement, type AgreementPayload } from "@/lib/characters-api"
import { useTranslation } from "@/hooks/use-translation"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agreementKey: string
  onConfirm: (version: number) => Promise<void> | void
  intro?: string | null
}

export function AgreementConfirmDialog({ open, onOpenChange, agreementKey, onConfirm, intro }: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const [agreement, setAgreement] = useState<AgreementPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setAccepted(false)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchAgreement(agreementKey)
      .then((data) => {
        if (!cancelled) setAgreement(data)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, agreementKey])

  const handleConfirm = async () => {
    if (!agreement || !accepted) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(agreement.version)
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[100] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[101] flex max-h-[90vh] w-[min(96vw,640px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated">
          <div className="flex items-start justify-between border-b border-ui-border p-5 pb-4">
            <Dialog.Title className="text-lg font-semibold text-ui-text-primary">
              {agreement?.title ?? t("agreements.defaultTitle")}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {intro && <p className="mb-3 text-sm text-ui-text-secondary">{intro}</p>}
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-ui-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("agreements.loading")}
              </div>
            ) : agreement ? (
              <p className="text-sm whitespace-pre-wrap text-ui-text-secondary">{agreement.body}</p>
            ) : (
              <p className="text-sm text-ui-status-failed">{error ?? t("agreements.loadError")}</p>
            )}
          </div>

          <div className="border-t border-ui-border p-5">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-ui-text-secondary">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-ui-border"
                checked={accepted}
                disabled={!agreement || submitting}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>{t("agreements.checkbox")}</span>
            </label>
            {error && agreement && <p className="mt-2 text-xs text-ui-status-failed">{error}</p>}
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
                {t("common:cancel")}
              </Button>
              <Button type="button" size="sm" disabled={!agreement || !accepted || submitting} onClick={handleConfirm}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    {t("agreements.submitting")}
                  </>
                ) : (
                  t("agreements.confirm")
                )}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
